import dayjs from 'dayjs';
import Datepicker from 'react-tailwindcss-datepicker';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { useEffect, useState } from "react";
import { fetchClient } from '../../util/fetchClient.js';
import { getStartOfQuarter, getStartOfHalfYear } from '../../util/date.js';
import RoenBranchSelector from "../_Shared/RoenBranchSelector.js";
import { ArrowLongRightIcon } from '@heroicons/react/24/solid';

const DEFAULT_BRANCH = { id: 'all', name: 'Todas' };

const formatDate = date => dayjs(date).format('DD/MM/YYYY');

const fetchBranches = async () => {
  const { body: branches } = await fetchClient('/v1/branches');
  const data = branches?.data || [];
  return [
    DEFAULT_BRANCH,
    ...data,
  ];
};

const fetchIncomeStatements = async ({ startTime, endTime, branchId }) => {
  const eod = dayjs().endOf('day');
  const startOfWeek = dayjs().startOf('week');
  const start = (startTime || startOfWeek).toISOString();
  const end = (endTime || eod).toISOString();
  const { body: incomeStatements } = await fetchClient(`/v1/income_statements?start_time=${start}&end_time=${end}&branch_id=${branchId}`);
  return { incomeStatements: incomeStatements?.data };
};

const classNames = (...classes) => {
  return classes.filter(Boolean).join(' ')
};

export default function IncomeStatement() {
  const [incomeStatements, setIncomeStatements] = useState([]);
  const [detailsVisibility, setDetailsVisibility] = useState({});

  const eod = dayjs().endOf('day');
  const startOfWeek = dayjs().startOf('week');
  const startOfMonth = dayjs().startOf('month');
  const startOfQuarter = dayjs(getStartOfQuarter(new Date()));
  const startOfHalfYear = dayjs(getStartOfHalfYear(new Date()));
  const startOfYear = dayjs().startOf('year');
  const [datesSelected, setDatesSelected] = useState({ startDate: startOfWeek, endDate: eod });

  const [branches, setBranches] = useState([]);
  const [branchSelected, setBranchSelected] = useState(DEFAULT_BRANCH);

  const handleDateChange = (newDates) => {
    setDatesSelected({
      startDate: dayjs(newDates.startDate).startOf('day'),
      endDate: dayjs(newDates.endDate).endOf('day'),
    });
  };

  useEffect(() => {
    async function getBranches() {
      if (!ignore) {
        const branches = await fetchBranches();
        setBranches(branches);
      }
    }

    let ignore = false;
    getBranches();
    return () => {
      ignore = true;
    }
  }, []);

  useEffect(() => {
    async function getIncomeStatements() {
      if (!ignore) {
        const { incomeStatements: updatedIncomeStatements } = await fetchIncomeStatements({ startTime: datesSelected.startDate && new Date(datesSelected.startDate), endTime: datesSelected.endDate && new Date(datesSelected.endDate), branchId: branchSelected.id });
        setIncomeStatements(updatedIncomeStatements);
        setDetailsVisibility({});
      }
    }

    let ignore = false;
    getIncomeStatements();
    return () => {
      ignore = true;
    }
  }, [datesSelected, branchSelected]);

  const toggleDetails = (incomeStatement) => {
    setDetailsVisibility({ ...detailsVisibility, [incomeStatement.name]: !detailsVisibility[incomeStatement.name] });
  };

  const colorStyling = (incomeStatement) => {
    if (incomeStatement.color === 'green') {
      return 'bg-green-100';
    }

    if (incomeStatement.color === 'red') {
      return 'bg-red-100';
    }

    if (incomeStatement.color === 'orange') {
      return 'bg-orange-100';
    }

    if (incomeStatement.color === 'purple') {
      return 'bg-purple-100';
    }

    if (incomeStatement.color === 'gray') {
      return 'bg-gray-200';
    }

    return '';
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <header>
        <div className="mx-auto flex flex-wrap items-center gap-6">
          <h1 className="text-base font-semibold leading-7 text-gray-900">Sucursal</h1>
          <div className="order-last flex w-full gap-x-8 text-sm font-semibold leading-6 sm:order-none sm:w-auto -ml-3">
            <RoenBranchSelector
              branchOptions={branches}
              branchSelected={branchSelected}
              onChange={setBranchSelected}
            />
          </div>
          <h1 className="text-base font-semibold leading-6 text-gray-900 -mr-3">Periodo</h1>
          <div className="order-last flex w-full gap-x-8 text-sm font-semibold leading-6 sm:order-none sm:w-auto">
            <Datepicker
              i18n={"es-mx"}
              inputClassName={"relative transition-all duration-300 py-1.5 pl-4 pr-10 w-full min-w-72 max-w-72 border-gray-300 dark:bg-slate-800 dark:text-white/80 dark:border-slate-600 rounded-lg tracking-wide font-light text-sm placeholder-gray-400 bg-white focus:ring disabled:opacity-40 disabled:cursor-not-allowed focus:border-indigo-500 focus:ring-indigo-500/20"}
              separator={"-"}
              primaryColor={"indigo"}
              useRange={true}
              displayFormat={"DD/MM/YYYY"}
              placeholder={`${formatDate(startOfWeek)} - ${formatDate(eod)}`}
              value={datesSelected}
              showShortcuts={true}
              onChange={handleDateChange}
              configs={{
                shortcuts: {
                  thisWeek: {
                    text: "Esta semana",
                    period: {
                      start: startOfWeek,
                      end: eod
                    },
                  },
                  thisMonth: {
                    text: "Este mes",
                    period: {
                      start: startOfMonth,
                      end: eod
                    },
                  },
                  thisQuarter: {
                    text: "Este trimestre",
                    period: {
                      start: startOfQuarter,
                      end: eod
                    },
                  },
                  thisHalfYear: {
                    text: "Este semestre",
                    period: {
                      start: startOfHalfYear,
                      end: eod
                    },
                  },
                  thisYear: {
                    text: "Este año",
                    period: {
                      start: startOfYear,
                      end: eod
                    },
                  },
                }
              }}
            />
          </div>
        </div>
      </header>

      <div className="mt-5 flow-root">
        <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
          <div className="grid grid-cols-3 inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="col-span-2 overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="top-0 z-10 py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                    >
                      Concepto
                    </th>
                    <th
                      scope="col"
                      className="top-0 z-10 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 pl-20"
                    >
                      Total
                    </th>
                    <th>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-300 bg-white">
                  {incomeStatements && incomeStatements.map((incomeStatement, _incomeStatementIdx) => (
                    <>
                      <tr className={classNames(incomeStatement.subcategories && incomeStatement.subcategories.length > 0 ? "hover:cursor-pointer" : "")} key={incomeStatement.name} onClick={() => toggleDetails(incomeStatement)}>
                        <td
                          className={classNames(
                            colorStyling(incomeStatement),
                            'whitespace-nowrap py-3 pl-2 pr-3 text-sm font-medium text-gray-900 sm:pl-6'
                          )}
                        >
                          <span className="lg:flex lg:items-center">
                            <span className="text-sm leading-6 text-gray-900" aria-hidden="true">
                              {incomeStatement.name}
                            </span>
                          </span>
                        </td>
                        <td
                          className={classNames(
                            colorStyling(incomeStatement),
                            'whitespace-nowrap px-3 py-3 text-sm text-gray-900'
                          )}
                        >
                          <div className="flex gap-x-6">
                            <div className="flex-auto">
                              <div className="inline-flex items-start gap-x-3 max-w-80">
                                <div className="text-sm leading-6 text-gray-900 text-right w-24 min-w-24">
                                  {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(incomeStatement.amount)}
                                </div>
                                <div className="text-sm leading-6 text-gray-500 text-right w-12 min-w-12">
                                  {incomeStatement.render_category_percentage && incomeStatement.percentage && `${incomeStatement.percentage}%`}
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td
                          className={classNames(
                            colorStyling(incomeStatement),
                            'whitespace-nowrap px-3 text-sm text-gray-500 pl-5'
                          )}
                        >
                          {
                            incomeStatement.subcategories
                            && incomeStatement.subcategories.length > 0
                            && !detailsVisibility[incomeStatement.name]
                            && <ChevronDownIcon className="h-5 w-5 text-gray-500 hover:cursor-pointer" aria-hidden="true" />
                          }
                          {
                            incomeStatement.subcategories
                            && incomeStatement.subcategories.length > 0
                            && detailsVisibility[incomeStatement.name]
                            && <ChevronUpIcon className="h-5 w-5 text-gray-500 hover:cursor-pointer" aria-hidden="true" onClick={() => toggleDetails(incomeStatement)} />
                          }
                        </td>
                      </tr>

                      {incomeStatement.subcategories && incomeStatement.subcategories.map((subcategoryStatement, _subcategoryStatementIdx) => (
                        <tr key={subcategoryStatement.name} className={detailsVisibility[incomeStatement.name] ? "visible" : "collapse"}>
                          <td
                            className={classNames(
                              'whitespace-nowrap py-3 pl-6 text-xs text-gray-500'
                            )}
                          >
                            <div className='inline-flex pl-2'>
                              <ArrowLongRightIcon className="h-4 w-4 text-gray-500" aria-hidden="true" />
                              <span className="pl-3">
                                {subcategoryStatement.name}
                              </span>
                            </div>
                          </td>
                          <td
                            className={classNames(
                              'whitespace-nowrap py-3 px-3 text-xs text-gray-500'
                            )}
                          >
                            <div className="flex gap-x-6">
                              <div className="flex-auto">
                                <div className="flex items-start gap-x-3">
                                  <div className="text-xs leading-6 text-gray-500 text-right w-24 min-w-24">
                                    {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(subcategoryStatement.amount)}
                                  </div>
                                  <div className="text-xs leading-6 text-gray-400 text-right w-12 min-w-12">
                                    {incomeStatement.render_subcategory_percentage && subcategoryStatement.percentage && `${subcategoryStatement.percentage}%`}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div >
  )
}
